import useSWR, { mutate } from 'swr';

const swrOptions = {
    refreshInterval: 0,
    revalidateOnFocus: false,
    //dedupingInterval:???
};

const digitalFormsBaseUrl = 'DigitalForms';
const getApplicationDetailsUrl = applicationId => `${digitalFormsBaseUrl}/GetFormApplicationDetails/${applicationId}`;
const getMetaUrl = applicationId => `${digitalFormsBaseUrl}/GetMeta/${applicationId}`;
const getQuestionsUrl = applicationId => `${digitalFormsBaseUrl}/GetQuestions/${applicationId}`;
const getLayoutUrl = applicationId => `${digitalFormsBaseUrl}/GetLayout/${applicationId}`;
const getRulesUrl = applicationId => `${digitalFormsBaseUrl}/GetRules/${applicationId}`;
const getActionsUrl = applicationId => `${digitalFormsBaseUrl}/GetActions/${applicationId}`;
const getAnswersUrl = applicationId => `${digitalFormsBaseUrl}/GetAnswers/${applicationId}`;
const getAutoFillUrl = applicationId => `${digitalFormsBaseUrl}/GetAutoFill/${applicationId}`;
const getFieldOptionsUrl = groupName => `${digitalFormsBaseUrl}/GetFieldOptions/${groupName}`;
const getGoodDaysFormularyUrl = (applicationId, diagnosisId, insuranceClassId) => `${digitalFormsBaseUrl}/GetGoodDaysFormulary/${applicationId}/${diagnosisId}/${insuranceClassId}`;
const postDigitalEnrollmentFormAnswersUrl = (patientId, applicationId) => `${digitalFormsBaseUrl}/${patientId}/SaveDigitalEnrollmentFormAnswers/${applicationId}`;
const submitDigitalEnrollmentFormUrl = (patientId, applicationId) => `${digitalFormsBaseUrl}/${patientId}/SubmitDigitalEnrollmentForm/${applicationId}`;

const lifeSciencesApplicationsBaseUrl = 'LifeSciences/applications';
const lifeSciencesApplicationsDeFormBaseUrl = 'LifeSciencesDeForm/applications';
const getIntegrationDeFormUrl = applicationId => `${lifeSciencesApplicationsDeFormBaseUrl}/${applicationId}/getIntegrationDeForm`;
const getIsIntegrationDeFormUrl = applicationId => `${lifeSciencesApplicationsDeFormBaseUrl}/${applicationId}/getIsJsonIntegrationDeForm`;
const getSetupApplicationTransmitUrl = applicationId => `${lifeSciencesApplicationsBaseUrl}/${applicationId}/setupApplicationTransmit`;
const getStartApplicationTransmitUrl = applicationId => `${lifeSciencesApplicationsBaseUrl}/${applicationId}/startApplicationTransmit`;
const getStatusApplicationTransmitUrl = applicationId => `${lifeSciencesApplicationsBaseUrl}/${applicationId}/getTransmitApplicationStatus`;

export const getAllDigitalFormsData = async(appId, fetcher) => {

    let isDeFormUrl = getIsIntegrationDeFormUrl(appId)
    let shouldLoadJson = await fetcher(isDeFormUrl);

    let totalForm = {}

    if(shouldLoadJson)
    {
        totalForm = await getNewJsonFromFile(fetcher, appId)
    }
    else
    {
        totalForm =  await getOriginalJsonFromDb(fetcher, appId)
    }

    return totalForm;
}

const formatAnswers = (records) => {
    const result = {};
    records.forEach(r => result[r.dataName] = r.answer);
    return result;
}

export const useGoodDaysFormulary = (applicationId, diagnosisId, insuranceClassId, fetcher) => {
    const { data, error } = useSWR(
        getGoodDaysFormularyUrl(applicationId, diagnosisId, insuranceClassId),
        fetcher,
        { ...swrOptions, dedupingInterval: 360000 }
    );

    return {
        gooddaysFormulary: data,
        isLoading: !error && !data,
        isError: error
    };
}

export const saveDigitalFormAnswers = async (axiosPost, patientId, applicationId, answers, formAnswers) => {
    if (formAnswers) mutate(getAnswersUrl(applicationId), [...formAnswers], false);
    return await axiosPost(postDigitalEnrollmentFormAnswersUrl(patientId, applicationId), answers);
}

export const useSetupTransmitApplication = (axiosPost, applicationId) => {
    const { data, error } = useSWR(
        getSetupApplicationTransmitUrl(applicationId),
        axiosPost,
        { ...swrOptions, dedupingInterval: 60000 }
    );

    return {
        setupTransmissionCalls: data,
        isLoading: !error && !data,
        isError: error
    };
}

export const useStartTransmitApplication = (axiosPost, applicationId, shouldFetch) => {
    const { data, error } = useSWR(
        shouldFetch ? getStartApplicationTransmitUrl(applicationId) : null,
        axiosPost,
        { ...swrOptions, dedupingInterval: 60000 }
    );

    return {
        startTransmissionCalls: data,
        isLoading: !error && !data,
        isError: error
    };
}

export const submitDigitalEnrollmentForm = async (axiosPut, patientId, applicationId, formAnswers, pdf) => {
    const formData = new FormData();
    formData.append('serializedFormAnswers', JSON.stringify(formAnswers));
    formData.append('digitalEnrollmentPdf', pdf);

    await axiosPut(submitDigitalEnrollmentFormUrl(patientId, applicationId), formData);
}

export const useGetTransmitApplicationStatus = (axiosPost, applicationId, shouldFetch) => {
    const { data, error } = useSWR(
        shouldFetch ? getStatusApplicationTransmitUrl(applicationId) : null,
        axiosPost,
        { ...swrOptions, refreshInterval: 500 }
    );

    return {
        statusTransmissionCalls: data,
        isLoading: !error && !data,
        isError: error
    };
}

export const mutateAllTransmissionCalls = (applicationId) => {
    mutate(getSetupApplicationTransmitUrl(applicationId));
    mutate(getStartApplicationTransmitUrl(applicationId));
    mutate(getStatusApplicationTransmitUrl(applicationId));
}

const getOriginalJsonFromDb = async(fetcher, appId) => {

    const questionUrl = getQuestionsUrl(appId);
    const layoutUrl = getLayoutUrl(appId);
    const rulesUrl = getRulesUrl(appId);
    const actionsUrl = getActionsUrl(appId);
    const answersUrl = getAnswersUrl(appId);
    const autoUrl = getAutoFillUrl(appId);
    const detailsUrl = getApplicationDetailsUrl(appId);
    const metaUrl = getMetaUrl(appId);


    const questions = await fetcher(questionUrl);
    const layout = await fetcher(layoutUrl);
    const rules = await fetcher(rulesUrl);
    const actions = await fetcher(actionsUrl);

    const details = await fetcher(detailsUrl);
    const meta = await fetcher(metaUrl);

    const existing = await fetcher(answersUrl);
    const answers = formatAnswers(existing);

    const autofill = await fetcher(autoUrl);

    let originalTotal = {questions, layout, rules, actions, answers, autofill, details, meta}

    return originalTotal;
}

const getNewJsonFromFile = async(fetcher, appId) => {

    try
    {
        const staticDataUrl = getIntegrationDeFormUrl(appId);

        const answersUrl = getAnswersUrl(appId);
        const autoUrl = getAutoFillUrl(appId);
        const detailsUrl = getApplicationDetailsUrl(appId);
        const metaUrl = getMetaUrl(appId);

        const details = await fetcher(detailsUrl);
        const meta = await fetcher(metaUrl);

        const existing = await fetcher(answersUrl);
        const answers = formatAnswers(existing);

        const autofill = await fetcher(autoUrl);

        let jsonData = {}

        jsonData = await fetcher(staticDataUrl);

        if(jsonData)
        {
            const { questions, layout, rules, actions } = jsonData;

            let newTotal = {questions, layout, rules, actions, answers, autofill, details, meta}

            return newTotal;
        }
        return {};
    }
    catch (e) {
        //Don't need to go any further
        return {};
    }
}
